import { useState } from 'react'
import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query'
import { useCursorPagination } from '@kolab/dms-base/hooks'
import type { PageableQuery, ReportPage } from '../api/types'

/**
 * Page-at-a-time (Previous / Next / First) server pagination for the device
 * activity reports. Page tokens are opaque, so there is no jump-to-page;
 * dms-base's useCursorPagination keeps the token history for Previous.
 *
 * `resetKey` identifies the result set (filters + sort + page size). When it
 * changes the pager returns to page 1 in the same render, so a stale page
 * token is never sent with new filters.
 */
export interface UseReportPaginationOptions<T> {
  queryKey: (page: PageableQuery) => QueryKey
  queryFn: (page: PageableQuery) => Promise<ReportPage<T>>
  pageSize: number
  resetKey: string
  enabled?: boolean
}

export function useReportPagination<T>({ queryKey, queryFn, pageSize, resetKey, enabled = true }: UseReportPaginationOptions<T>) {
  const pager = useCursorPagination({ pageSize })
  const [pagedKey, setPagedKey] = useState(resetKey)
  const stale = pagedKey !== resetKey
  if (stale) {
    // Adjusting state while rendering: React re-renders before committing.
    setPagedKey(resetKey)
    pager.reset()
  }

  const page: PageableQuery = { pageSize, pageToken: stale ? undefined : pager.cursor }
  const query = useQuery({
    queryKey: queryKey(page),
    queryFn: () => queryFn(page),
    placeholderData: keepPreviousData,
    enabled,
  })

  const data = query.data
  const nextToken = data?.nextPageToken
  const currentPage = stale ? 1 : pager.currentPage
  const totalCount = data?.totalCount ?? 0

  return {
    query,
    items: data?.items ?? [],
    totalCount,
    timezone: data?.timezone,
    currentPage,
    totalPages: Math.max(1, Math.ceil(totalCount / pageSize)),
    /** 0-based offset of the first row on this page, for the S.N column. */
    offset: (currentPage - 1) * pageSize,
    hasNext: !query.isPlaceholderData && typeof nextToken === 'string' && nextToken.length > 0,
    hasPrevious: !stale && pager.hasPrevious,
    goToNext: () => nextToken && pager.goToNext(nextToken),
    goToPrevious: pager.goToPrevious,
    goToFirst: pager.reset,
  }
}
